import React, { useState } from 'react';
import { Button, IconButton, Menu, MenuItem, StatusPill } from './controls';
import { ActionSheet } from './Dialog';
import { Icon, IconName } from './icons';
import { Translate } from './i18n';
import { AMBER, DIM, DIVIDER, INK, MUTED, RED, StatusInfo, TEAL, TEXT, TINT, card } from './tokens';

export interface Fact {
  key: string;
  label: string;
  /** The value; when the step has not happened yet, `pending` text instead. */
  value?: React.ReactNode;
  pending?: string;
  /** Second lines under the value (who, contact details). */
  lines?: string[];
  /** Desktop: span two columns (the supplier with its contact line). */
  wide?: boolean;
  /** Desktop: start the timeline row. */
  newRow?: boolean;
  /** Desktop: one second line per entry instead of one line joined with " · " (the supplier keeps the joined line). */
  stackLines?: boolean;
  numeric?: boolean;
}

export interface NextStep {
  tone: 'waiting' | 'progress' | 'stopped';
  text: string;
  /** Header actions for the viewer (primary last); full width under the text on phone. */
  actions?: React.ReactNode;
}

const TONE: Record<NextStep['tone'], { color: string; icon: IconName }> = {
  waiting: { color: AMBER, icon: 'clock' },
  progress: { color: TEAL, icon: 'arrowRight' },
  stopped: { color: RED, icon: 'alert' },
};

interface DocumentHeaderProps {
  phone: boolean;
  t: Translate;
  backLabel: string;
  onBack: () => void;
  title: string;
  /** An order status id; other documents pass `status`. */
  statusId?: unknown;
  status?: StatusInfo;
  nextStep?: NextStep | null;
  facts: Fact[];
  onPrint?: () => void;
  onDownload?: () => void;
  moreItems?: MenuItem[];
}

const valueStyle = (pending: boolean, numeric?: boolean): React.CSSProperties => ({
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: pending ? 500 : 600,
  color: pending ? MUTED : INK,
  fontVariantNumeric: numeric ? 'tabular-nums' : undefined,
});

export const DocumentHeader: React.FC<DocumentHeaderProps> = ({ phone, t, backLabel, onBack, title, statusId, status, nextStep, facts, onPrint, onDownload, moreItems = [] }) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const titleId = phone ? 'pu-doc-title-phone' : 'pu-doc-title';
  const sheetItems: MenuItem[] = [
    ...(onPrint ? [{ key: 'print', label: t('Print'), icon: 'print' as IconName, onSelect: onPrint }] : []),
    ...(onDownload ? [{ key: 'pdf', label: t('Download PDF'), icon: 'download' as IconName, onSelect: onDownload }] : []),
    ...moreItems,
  ];
  const back = (
    <a
      href="#"
      className="pu-link"
      onClick={(event) => {
        event.preventDefault();
        onBack();
      }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: phone ? '44px' : '28px', fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: TEXT, textDecoration: 'none' }}
    >
      <Icon name="chevronLeft" color={DIM} />
      {backLabel}
    </a>
  );
  const tone = nextStep ? TONE[nextStep.tone] : null;

  return (
    <section aria-labelledby={titleId} style={{ ...card, display: 'flex', flexDirection: 'column', gap: phone ? '14px' : '16px', padding: phone ? '16px' : '20px' }}>
      <div className="pu-no-print" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: phone ? '8px' : '12px' }}>
        {back}
        {phone ? (
          sheetItems.length > 0 && (
            <>
              <Button phone aria-haspopup="dialog" onClick={() => setSheetOpen(true)} style={{ height: '44px', padding: '0 16px' }}>
                {t('More')}
              </Button>
              <ActionSheet open={sheetOpen} title={t('More')} closeLabel={t('Close')} items={sheetItems} onClose={() => setSheetOpen(false)} />
            </>
          )
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
            {onPrint && <Button onClick={onPrint}>{t('Print')}</Button>}
            {onDownload && <Button onClick={onDownload}>{t('Download PDF')}</Button>}
            {moreItems.length > 0 && (
              <Menu
                label={t('More actions')}
                items={moreItems}
                trigger={(props) => <IconButton icon="moreVertical" label={t('More actions')} bordered {...props} />}
              />
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: phone ? '8px' : '12px' }}>
        <h1 id={titleId} style={{ fontSize: phone ? '20px' : '24px', lineHeight: phone ? '28px' : '32px', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>
          {title}
        </h1>
        {/* A page that is not a document (a supplier catalogue) has no status. */}
        {(status || statusId !== undefined) && <StatusPill statusId={statusId} status={status} t={t} />}
      </div>

      {nextStep && tone && (
        <div
          className={nextStep.actions ? 'pu-no-print' : undefined}
          style={phone ? { display: 'flex', flexDirection: 'column', gap: '10px' } : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
        >
          <p style={{ display: 'flex', alignItems: phone ? 'flex-start' : 'center', gap: '8px', fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: tone.color, fontVariantNumeric: 'tabular-nums' }}>
            <Icon name={tone.icon} style={phone ? { marginTop: '2px' } : undefined} />
            {nextStep.text}
          </p>
          {nextStep.actions && (
            <div style={phone ? { display: 'flex', flexDirection: 'column', gap: '8px' } : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>{nextStep.actions}</div>
          )}
        </div>
      )}

      {phone ? (
        <dl>
          {facts.map((fact, index) => {
            const pending = fact.value === undefined || fact.value === null || fact.value === '';
            return (
              <div
                key={fact.key}
                style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', padding: index === facts.length - 1 ? '10px 0 0' : '10px 0', borderTop: `1px solid ${DIVIDER}` }}
              >
                <dt style={{ flex: 'none', fontSize: '12.5px', lineHeight: '20px', fontWeight: 500, color: MUTED }}>{fact.label}</dt>
                <dd style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'right' }}>
                  <span style={{ display: 'block', ...valueStyle(pending, fact.numeric) }}>{pending ? fact.pending ?? '—' : fact.value}</span>
                  {(fact.lines ?? []).filter(Boolean).map((line, index) => (
                    <span key={index} style={{ display: 'block', fontSize: '12.5px', lineHeight: '18px', color: MUTED, overflowWrap: 'anywhere' }}>
                      {line}
                    </span>
                  ))}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
        <dl
          style={{
            maxWidth: '1180px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '16px 24px',
            padding: '16px',
            background: TINT,
            border: `1px solid ${DIVIDER}`,
            borderRadius: '12px',
          }}
        >
          {facts.map((fact) => {
            const pending = fact.value === undefined || fact.value === null || fact.value === '';
            const lines = (fact.lines ?? []).filter(Boolean);
            return (
              <div key={fact.key} style={{ minWidth: 0, gridColumn: fact.wide ? 'span 2' : undefined, gridColumnStart: fact.newRow ? 1 : undefined }}>
                <dt style={{ fontSize: '12.5px', lineHeight: '18px', fontWeight: 500, color: MUTED }}>{fact.label}</dt>
                <dd style={{ margin: '2px 0 0', ...valueStyle(pending, fact.numeric) }}>{pending ? fact.pending ?? '—' : fact.value}</dd>
                {(fact.stackLines ? lines : lines.length > 0 ? [lines.join(' · ')] : []).map((line, index) => (
                  <dd key={index} title={line} style={{ fontSize: '12.5px', lineHeight: '18px', color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {line}
                  </dd>
                ))}
              </div>
            );
          })}
        </dl>
      )}
    </section>
  );
};
